/* ==========================================================================
   ASJ CUSTOM - Inline PDF viewer for product data sheets.
   Renders a re-hosted (Shopify CDN) PDF directly into the page with PDF.js so
   customers can read Technical / Safety data sheets without leaving the site,
   including on mobile where native <iframe> PDF embedding does not work.

   - Progressive & lazy: the ~1.3MB PDF.js library is injected only when a
     viewer actually scrolls into view (IntersectionObserver), which for the
     Technical Data tab means when that tab is opened. Nothing loads otherwise.
   - Each viewer is a <div data-asj-pdf data-asj-pdf-url data-asj-pdf-lib
     data-asj-pdf-worker>. A visible "Open / download" link is always present
     in the markup as a no-JS / failure fallback.
   - Pages render to <canvas> sized to the container width (responsive), inside
     a scrollable frame. Isolated; safe to remove with its CSS + markup.
   ========================================================================== */
(function () {
  'use strict';

  var MAX_PAGES = 30;       // safety cap; data sheets are far shorter
  var MAX_DPR = 2;          // cap device-pixel-ratio to limit canvas memory
  var libPromise = null;

  function loadLib(src, worker) {
    if (libPromise) return libPromise;
    libPromise = new Promise(function (resolve, reject) {
      if (window.pdfjsLib) { resolve(window.pdfjsLib); return; }
      var s = document.createElement('script');
      s.src = src;
      s.onload = function () {
        if (window.pdfjsLib) {
          try { window.pdfjsLib.GlobalWorkerOptions.workerSrc = worker; } catch (e) {}
          resolve(window.pdfjsLib);
        } else {
          reject(new Error('pdfjsLib not available after load'));
        }
      };
      s.onerror = function () { reject(new Error('PDF.js failed to load')); };
      document.head.appendChild(s);
    });
    return libPromise;
  }

  function renderViewer(el) {
    if (el.dataset.asjPdfDone) return;
    el.dataset.asjPdfDone = '1';

    var url = el.getAttribute('data-asj-pdf-url');
    var lib = el.getAttribute('data-asj-pdf-lib');
    var worker = el.getAttribute('data-asj-pdf-worker');
    var pagesWrap = el.querySelector('.asj-pdf__pages');
    var status = el.querySelector('.asj-pdf__status');
    if (!url || !pagesWrap) return;

    function fail(err) {
      if (status) {
        status.textContent = 'Preview unavailable here — use the Open / download link above to view the PDF.';
      }
      if (window.console && console.warn) console.warn('ASJ PDF viewer:', err);
    }

    loadLib(lib, worker).then(function (pdfjsLib) {
      return pdfjsLib.getDocument({ url: url }).promise;
    }).then(function (pdf) {
      var total = Math.min(pdf.numPages, MAX_PAGES);
      var dpr = Math.min(window.devicePixelRatio || 1, MAX_DPR);
      var chain = Promise.resolve();

      for (var n = 1; n <= total; n++) {
        (function (pageNum) {
          chain = chain.then(function () {
            return pdf.getPage(pageNum).then(function (page) {
              var containerWidth = pagesWrap.clientWidth || el.clientWidth || 800;
              var base = page.getViewport({ scale: 1 });
              var scale = containerWidth / base.width;
              var viewport = page.getViewport({ scale: scale });

              var canvas = document.createElement('canvas');
              canvas.className = 'asj-pdf__page';
              canvas.setAttribute('role', 'img');
              canvas.setAttribute('aria-label', 'Data sheet page ' + pageNum + ' of ' + total);
              canvas.width = Math.floor(viewport.width * dpr);
              canvas.height = Math.floor(viewport.height * dpr);
              var ctx = canvas.getContext('2d');
              ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
              pagesWrap.appendChild(canvas);

              return page.render({ canvasContext: ctx, viewport: viewport }).promise;
            });
          });
        })(n);
      }

      return chain.then(function () {
        if (status) status.remove();
      });
    }).catch(fail);
  }

  function init() {
    var viewers = Array.prototype.slice.call(document.querySelectorAll('[data-asj-pdf]'));
    if (!viewers.length) return;

    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            io.unobserve(entry.target);
            renderViewer(entry.target);
          }
        });
      }, { rootMargin: '250px 0px' });
      viewers.forEach(function (v) { io.observe(v); });
    } else {
      viewers.forEach(renderViewer);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
